/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * StudentDetails
 *
 * @file StudentDetails.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, Typography } from '@material-ui/core';
import UnderlineTextButton from '../../common/UnderlineTextButton ';
import VerticalDivider from '../../common/VerticalDivider';
import SectionTabs from '../../common/SectionTabs';
import ReviewList from '../../common/ReviewList';
import StudentDetailsPopup from './StudentDetailsPopup';

const useStyles = makeStyles(() => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'flex-start',
    width: '100%',
    background: '#fff',
    padding: 16,
    borderRadius: 8
  },
  sectionInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%'
  },
  sectionTitle: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    height: 32,
    width: '100%'
  },
  avatar: {
    width: 32,
    height: 32
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    flexGrow: 1
  },
  titleText: {
    color: '#171717',
    fontFamily: "'PlusJakartaSans-SemiBold', sans-serif",
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 600
  },
  links: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexGrow: 1,
    borderTop: '1px solid transparent',
    borderBottom: '1px solid transparent'
  },
  progress: {
    borderTop: '1px solid transparent',
    width: 675,
    marginTop: 12
  },
  labelText: {
    color: '#0d004d',
    fontFamily: "'PlusJakartaSans-Medium', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '22px',
    marginBottom: 8
  },
  progressBarTrack: {
    borderRadius: 1000,
    height: 6,
    backgroundColor: 'rgba(81, 46, 171, 0.15)'
  },
  progressBarFill: {
    borderRadius: 1000,
    height: 6,
    backgroundColor: '#512eab',
    transition: 'width 0.3s ease'
  },
  difficultTopics: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  }
}));

const StudentDetails = ({
  id,
  name,
  avatarSrc,
  completedAssignments,
  totalAssignments,
  helpTopics,
  onViewReport,
  onViewInsights,
  progressTabLabel,
  onDifficultTopicClick,
  assignmentTitle = 'Assignment'
}) => {
  const classes = useStyles();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const progressPercent =
    totalAssignments > 0 ? (completedAssignments / totalAssignments) * 100 : 0;

  const handleViewReport = () => {
    setIsPopupOpen(true);
    if (onViewReport) {
      onViewReport();
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const selectedStudent = {
    name,
    topics: helpTopics || []
  };

  function renderTitle() {
    if (!name) return null;

    return <Typography className={classes.titleText}>{name}</Typography>;
  }

  function renderAvatar() {
    if (avatarSrc) {
      return <Avatar alt={name} className={classes.avatar} src={avatarSrc} />;
    } if (name) {
      return <Avatar className={classes.avatar}>{name[0]}</Avatar>;
    }

    return <Avatar className={classes.avatar}>?</Avatar>;
  }

  return (
    <div className={classes.section}>
      <div className={classes.sectionInner}>
        <div>
          <div className={classes.sectionTitle}>
            {renderAvatar()}
            <div className={classes.title}>{renderTitle()}</div>
            <div className={classes.links}>
              <UnderlineTextButton text="View detailed report" onClick={handleViewReport} />
              <VerticalDivider />
              <UnderlineTextButton text="View insights" onClick={onViewInsights} />
            </div>
          </div>
          <div className={classes.progress}>
            <Typography className={classes.labelText}>
              {completedAssignments}/{totalAssignments} assignments completed
            </Typography>
            <div className={classes.progressBarTrack}>
              <div className={classes.progressBarFill} style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>
        <div>
          <SectionTabs tab1Label={progressTabLabel} />

          <div style={{ marginTop: 8 }} className={classes.difficultTopics}>
            <ReviewList
              list={helpTopics}
              onDifficultTopicClick={onDifficultTopicClick}
              assignmentId={id}
              isStudentView
            />
          </div>
        </div>
      </div>

      <StudentDetailsPopup
        open={isPopupOpen}
        onClose={handleClosePopup}
        selectedStudent={selectedStudent}
        assignmentTitle={assignmentTitle}
        onSupportClick={(student) => {
          console.log('Support clicked for:', student);
        }}
      />
    </div>
  );
};

StudentDetails.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  avatarSrc: PropTypes.string,
  completedAssignments: PropTypes.number,
  totalAssignments: PropTypes.number,
  onViewReport: PropTypes.func,
  onViewInsights: PropTypes.func,
  progressTabLabel: PropTypes.string,
  helpTopics: PropTypes.array,
  onDifficultTopicClick: PropTypes.func,
  assignmentTitle: PropTypes.string
};

StudentDetails.defaultProps = {
  id: 0,
  name: '',
  avatarSrc: '',
  completedAssignments: 0,
  totalAssignments: 20,
  onViewReport: () => {},
  onViewInsights: () => {},
  progressTabLabel: 'Areas needing support (3)',
  helpTopics: [],
  onDifficultTopicClick: () => {},
  assignmentTitle: 'Assignment'
};

export default StudentDetails;
